import React, {useState, useEffect} from 'react';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';

import {
  Anchor,
  addToast,
  Text,
  Icon,
  WithTooltip,
  IconButton,
  Button,
  Modal,
  Dialogue
} from '@albert-io/atomic';

import {createLtiLineItemQuery, deleteLtiLineItemQuery} from './LtiCell.queries';
import './lti-cell.scss';

import {withStopPropagation, getLtiPlatformData} from './utils';

const useLtiSync = (assignmentId, classroomId, schoolId, hasLtiLineItemId) => {
  const [hasSyncedWithLti, setHasSyncedWithLti] = useState(hasLtiLineItemId);
  const [resyncCompleted, setResyncCompleted] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);

  const handleLtiSyncError = (e) => {
    const {errors} = e.response.body;
    const error = errors[0];
    if (error.code === 'Errors.ThirdPartyHTTPRequestError') {
      addToast({
        type: 'danger',
        title: 'Incorrect Permissions',
        message:
          "Your school's technical admin needs to adjust Albert's permissions on your LMS to include grades and assignments."
      });
    } else {
      addToast({
        type: 'danger',
        message: 'There was an error syncing your classroom data.'
      });
    }
  };

  const createLtiLineItem = async () => {
    try {
      await createLtiLineItemQuery(assignmentId, classroomId, schoolId);
      return true;
    } catch (e) {
      handleLtiSyncError(e);
      return false;
    }
  };

  const deleteLtiLineItem = async () => {
    try {
      await deleteLtiLineItemQuery(assignmentId, classroomId, schoolId);
      return true;
    } catch (e) {
      handleLtiSyncError(e);
      return false;
    }
  };

  const syncLti = async () => {
    setIsRequestPending(true);
    const syncSuccess = await createLtiLineItem();
    setHasSyncedWithLti(syncSuccess);
    setIsRequestPending(false);
    return syncSuccess;
  };

  const resyncLti = async () => {
    setIsRequestPending(true);
    const deleteSuccess = await deleteLtiLineItem();
    const syncSuccess = deleteSuccess ? await createLtiLineItem() : false;
    setHasSyncedWithLti(syncSuccess);
    setResyncCompleted(syncSuccess);
    setIsRequestPending(false);
  };

  return {
    hasSyncedWithLti,
    resyncCompleted,
    setResyncCompleted,
    isRequestPending,
    syncLti,
    resyncLti
  };
};

interface LtiSyncModalProps {
  ltiPlatformName: string;
  syncLti: () => Promise<boolean>;
  isRequestPending: boolean;
  onDismiss: () => void;
}

const LtiSyncModal = ({
  ltiPlatformName,
  syncLti,
  isRequestPending,
  onDismiss
}: LtiSyncModalProps) => {
  const onSync = async () => {
    const success = await syncLti();
    if (success) {
      onDismiss();
    }
  };

  return (
    <Modal ariaLabel='Confirm Lti Sync Modal' handleClose={onDismiss}>
      {({CloseButtonWrapper}) => (
        <Dialogue inModal hideCloseBtn className='lti-cell__sync-modal'>
          <Dialogue.Body className='lti-cell__sync-modal--body'>
            <div className='lti-cell__sync-modal--info-icon'>
              <Icon icon='info-circle' iconStyle='light' />
            </div>
            <Text className='u-color_slate-800 u-text-align_center'>
              Grades will update in {ltiPlatformName} once explanations are made available to
              students in Albert, depending on your assignment settings.
            </Text>
            <Text italic className='u-color_slate-500 u-text-align_center'>
              You can sync this assignment at any time from your class assignment list.
            </Text>
          </Dialogue.Body>
          <div className='lti-cell__sync-modal--button-group'>
            <CloseButtonWrapper>
              <Button
                variant='outlined'
                className='lti-cell__sync-modal--button'
                onClick={onDismiss}
              >
                I&apos;ll do this later
              </Button>
            </CloseButtonWrapper>
            <Button
              disabled={isRequestPending}
              className='lti-cell__sync-modal--button'
              onClick={onSync}
            >
              Sync
            </Button>
          </div>
        </Dialogue>
      )}
    </Modal>
  );
};

interface LtiCellProps {
  assignment: AssignmentModelV3;
  classroom: ClassroomModelV1;
}

export const LtiCell = ({assignment, classroom}: LtiCellProps) => {
  const schoolHasLtiDeployment = classroom.getMeta().hasLtiDeployment();
  const ltiPlatformName = classroom.getMeta().getLtiPlatformName();
  const classroomConnectedToLti = classroom.hasLtiCourse();
  const school = classroom.getSchool();

  const [showSyncModal, setShowSyncModal] = useState(false);

  const {
    hasSyncedWithLti,
    resyncCompleted,
    setResyncCompleted,
    isRequestPending,
    syncLti,
    resyncLti
  } = useLtiSync(
    assignment.getId(),
    classroom.getId(),
    school.getId(),
    !!assignment.getLtiLineItemId()
  );

  const anchorText = 'Learn how to connect tools like Canvas, Schoology, etc';
  const {ltiPlatformDisplayName, ltiPlatformHelpLink} = getLtiPlatformData(ltiPlatformName);

  useEffect(() => {
    let timer;

    if (resyncCompleted) {
      timer = setTimeout(() => {
        setResyncCompleted(false);
      }, 3000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [resyncCompleted]);

  if (!schoolHasLtiDeployment) {
    return (
      <WithTooltip
        theme='light'
        placement='left'
        content={
          <div>
            {'Cannot Sync Grades. Integration not configured. '}
            <Anchor
              target='_blank'
              to='https://help.albert.io/en/articles/1885028-do-you-integrate-with-any-lms-systems'
            >
              {anchorText}
            </Anchor>
          </div>
        }
        wrapperProps={{className: 'u-justify-self_center'}}
      >
        <IconButton
          onClick={withStopPropagation(() => {})}
          icon='info-circle'
          iconStyle='regular'
          aria-hidden={false}
        />
      </WithTooltip>
    );
  }

  if (!classroomConnectedToLti) {
    return (
      <WithTooltip
        theme='light'
        placement='left'
        content={
          <div>
            {'Cannot Sync Grades. Classrooms not connected. '}
            <Anchor target='_blank' to={ltiPlatformHelpLink}>
              {anchorText}
            </Anchor>
          </div>
        }
        wrapperProps={{className: 'u-justify-self_center'}}
      >
        <IconButton
          onClick={withStopPropagation(() => {})}
          icon='info-circle'
          iconStyle='regular'
          aria-hidden={false}
        />
      </WithTooltip>
    );
  }

  if (hasSyncedWithLti) {
    return (
      <Text
        color='green'
        bold
        size='m'
        className='u-justify-self_center u-display_flex u-align-items_center u-color_green-600'
      >
        {'Synced '}
        {resyncCompleted ? (
          <Icon className='u-mar-l_1' icon='check' color='inherit' />
        ) : (
          <WithTooltip content='Re-sync'>
            <Button
              onClick={withStopPropagation(resyncLti)}
              disabled={isRequestPending}
              size='s'
              color='primary'
              roundness='circle'
              variant='text'
            >
              <Icon icon='arrows-rotate' spin={isRequestPending} />
            </Button>
          </WithTooltip>
        )}
      </Text>
    );
  }

  return (
    <div className='u-justify-self_center'>
      <Button
        disabled={isRequestPending}
        size='s'
        variant='outlined'
        color='primary'
        className='lti-cell__button'
        onClick={withStopPropagation(() => {
          setShowSyncModal(true);
        })}
      >
        Sync
      </Button>

      {showSyncModal && (
        <LtiSyncModal
          ltiPlatformName={ltiPlatformDisplayName}
          syncLti={syncLti}
          isRequestPending={isRequestPending}
          onDismiss={() => {
            setShowSyncModal(false);
          }}
        />
      )}
    </div>
  );
};
